
import { Component, Input, OnInit, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { LookupService, MaterialLookup, MaterialSearchResult } from '../../../services/lookup.service';
import { SpacerService, CreateSpacerRequestDto, CreateSpacerMaterialDto, UpdateSpacerRedesignReasonRequestDto } from '../../../services/spacer.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FinalReportDialogComponent } from '../../final-report-dialog/final-report-dialog.component';
import { SlurryService, SlurryBriefDto } from '../../../services/slurry.service';
import { ProjectDetailsService } from '../../../services/project-details.service';
import { LoggedUserService } from '../../../services/logged-user.service';
import { EditComponent } from './edit/edit.component';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CisProSearchComponent } from '../../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../../services/cispro-search.service';


interface DryMaterial {
  materialId?: number;
  name: string;
  specificGravity: number;  // Full precision specific gravity
  absVolume: number;        // Display only - calculated from specificGravity
  weight: number;
  volume: number;
  labWeight: number;
  cisproContainer: string;
}

interface LiquidMaterial {
  materialId?: number;
  name: string;
  volume: number;
  specificGravity: number;
  weight: number;
  labWeight: number;
  cisproContainer: string;
}

@Component({
  selector: 'app-create-spacer-info',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatDialogModule, MatButtonModule],
  templateUrl: './create-spacer-info.component.html',
  styleUrls: ['./create-spacer-info.component.css']
})
export class CreateSpacerInfoComponent implements OnInit, OnDestroy {
  projectId: number | null = null;
  projectName: string | null = null;
  redesignData: any;
  projectTypeId: number | null = null; 
  projectSlurries: SlurryBriefDto[] = [];
  serviceCompanyName: string = '';
  userEmail: string = '';
  private projectTypeIdSetFromNavigation = false;
  isBlendTest = false;

  // ── Additive autocomplete ───────────────────────────────────
  showBlendAdditiveSearch = false;
  blendAdditiveSearchText = '';
  blendAdditiveOptions: MaterialSearchResult[] = [];

  showAfterBlendAdditiveSearch = false;
  afterBlendAdditiveSearchText = '';
  afterBlendAdditiveOptions: MaterialSearchResult[] = [];

  showLiquidAdditiveSearch = false;
  liquidAdditiveSearchText = '';
  liquidAdditiveSearchOptions: MaterialSearchResult[] = [];

  private blendAdditiveSubject = new Subject<string>();
  private afterBlendAdditiveSubject = new Subject<string>();
  private liquidSearchSubject = new Subject<string>();
  private subs = new Subscription();

  // Material options for dropdowns
  blendBaseOptions: MaterialLookup[] = [];
  liquidBaseOptions: MaterialLookup[] = [];
  dryAdditiveOptions: MaterialLookup[] = [];
  liquidAdditiveOptions: MaterialLookup[] = [];

  // Cache for different material sets based on mode
  private standardBlendBaseOptions: MaterialLookup[] = [];
  private fractionBasedBlendBaseOptions: MaterialLookup[] = [];
  private standardLiquidBaseOptions: MaterialLookup[] = [];
  private fractionBasedLiquidBaseOptions: MaterialLookup[] = [];

  // Spacer Info fields
  waterFractionBased: boolean = false;
  waterFractionValue: number = 0;
  viscosifierValue: number = 0;
  blendBaseError = false;
  liquidBaseError = false;
  blendBaseWeightError = false;
  waterFractionError = false;
  viscosifierError = false;
  waterFractionTouched = false;
  viscosifierTouched = false;
  blendAdditiveWeightErrors: boolean[] = [];
  afterBlendAdditiveWeightErrors: boolean[] = [];
  liquidAdditiveVolumeErrors: boolean[] = [];
  labVolume: number = 800;
  totalDensity: number = 0.00;
  notes: string = '';

  // Store full precision total density for calculations
  private totalDensityFullPrecision: number = 0;

  // Blend CisPRO Container
  blendCisproContainer: string = '';

  // Dry Materials
  blendBase: DryMaterial = {
    materialId: undefined,
    name: '',
    specificGravity: 0,
    absVolume: 0,
    weight: null as any,
    volume: 0,
    labWeight: 0,
    cisproContainer: ''
  };

  additives: DryMaterial[] = [];

  // Blend Additives (for blend test mode - materials added before blend)
  blendAdditives: DryMaterial[] = [];

  // After Blend Additives (for blend test mode - materials added after blend)
  afterBlendAdditives: DryMaterial[] = [];

  // Liquid Materials
  rigFreshWater: LiquidMaterial = {
    materialId: undefined,
    name: '',
    volume: 0,
    specificGravity: 0,
    weight: 0,
    labWeight: 0,
    cisproContainer: ''
  };

  liquidAdditives: LiquidMaterial[] = [];

  // Store full precision values for calculations
  private blendBaseFullPrecisionVolume = 0;
  private blendBaseFullPrecisionWeight = 0;
  private blendBaseFullPrecisionAbsVolume = 0;
  private liquidBaseFullPrecisionVolume = 0;
  private liquidBaseFullPrecisionWeight = 0;
  private liquidBaseFullPrecisionSpecificGravity = 0;
  private additivesFullPrecisionVolumes: number[] = [];

  // Interpolation tables for salt additives
  private readonly sodiumChlorideTable = [
    { percent: 0, specificGravity: 3.35 },
    { percent: 5, specificGravity: 3.262174435 },
    { percent: 10, specificGravity: 3.118130369 },
    { percent: 15, specificGravity: 3.01628189 },
    { percent: 20, specificGravity: 2.935159394 },
    { percent: 25, specificGravity: 2.871962182 },
    { percent: 30, specificGravity: 2.81802862 },
    { percent: 35.14, specificGravity: 2.7724715756 }
  ];

  private readonly potassiumChlorideTable = [
    { percent: 0, specificGravity: 2.75 },
    { percent: 1, specificGravity: 2.753394936 },
    { percent: 4, specificGravity: 2.66773376 },
    { percent: 10, specificGravity: 2.592829789 },
    { percent: 15, specificGravity: 2.52732672 },
    { percent: 20, specificGravity: 2.495800815 },
    { percent: 25, specificGravity: 2.460000394 },
    { percent: 30, specificGravity: 2.4350511 },
    { percent: 31.58, specificGravity: 2.425212509 }
  ];

  private readonly SODIUM_CHLORIDE_DRY_SG = 2.15;
  private readonly POTASSIUM_CHLORIDE_DRY_SG = 1.99;
  private readonly WATER_DENSITY = 8.34;
  private readonly SPACER_TOTAL_VOLUME = 42; // gallons
  private readonly LB_PER_GAL_IN_G_PER_ML = 0.119826;

  constructor(
    private lookupService: LookupService,
    private spacerService: SpacerService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private slurryService: SlurryService,
    private projectDetailsService: ProjectDetailsService,
    private loggedUserService: LoggedUserService
  ) {
    // Get navigation state for redesign data
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      const state = navigation.extras.state;
      this.redesignData = navigation.extras.state['redesign'];
      this.projectId = navigation.extras.state['projectId'];
      this.projectName = navigation.extras.state['projectName'];
      const projectType = navigation.extras.state['projectType'];
      const slurries = navigation.extras.state['slurries'];
      const userEmail = navigation.extras.state['userEmail'];
      const serviceCompanyName = navigation.extras.state['serviceCompanyName'];
      if (state['blendTest'] !== undefined) {
        this.isBlendTest = state['blendTest'];
      }

      // Set projectTypeId from navigation state if available
      if (projectType !== undefined && projectType !== null) {
        this.projectTypeId = typeof projectType === 'number' ? projectType : 2;
        this.projectTypeIdSetFromNavigation = true;
      }

      // Set slurries from navigation state if available
      if (slurries && Array.isArray(slurries)) {
        this.projectSlurries = slurries;
      }

      // Set userEmail from navigation state if available
      if (userEmail) {
        this.userEmail = userEmail;
      }

      // Set serviceCompanyName from navigation state if available
      if (serviceCompanyName) {
        this.serviceCompanyName = serviceCompanyName;
      }
    }
  }

  /**
   * Calculate density from specific gravity with full precision.
   * Matches C# Material.Density property.
   */
  private calculateDensity(specificGravity: number): number {
    return specificGravity * this.WATER_DENSITY;
  }

  /**
   * Calculate absolute volume from specific gravity with full precision.
   * Matches C# Material.AbsoluteVolume property: 1 / Density
   */
  private calculateAbsoluteVolume(specificGravity: number): number {
    const density = this.calculateDensity(specificGravity);
    return density > 0 ? 1 / density : 0;
  }

  validateBaseSelection(): void {
    const blendBaseSelected = !!this.blendBase?.materialId;
    const liquidBaseSelected = !!this.rigFreshWater?.materialId;

    this.blendBaseError = !blendBaseSelected;
    this.liquidBaseError = !liquidBaseSelected;
  }

  validateBlendBaseWeight(): void {
    if (this.waterFractionBased) {
      this.blendBaseWeightError = false;
      return;
    }

    this.blendBaseWeightError =
      !!this.blendBase.materialId &&
      (!this.blendBase.weight || this.blendBase.weight <= 0);
  }

  validateBlendAdditiveWeight(index: number): void {
    // Validate based on which array is being used
    if (this.isBlendTest && this.blendAdditives.length > 0) {
      const additive = this.blendAdditives[index];
      if (additive) {
        this.blendAdditiveWeightErrors[index] =
          additive.weight == null ||
          additive.weight === '' as any ||
          isNaN(Number(additive.weight)) ||
          Number(additive.weight) <= 0;
      }
    } else if (!this.isBlendTest && this.additives.length > 0) {
      const additive = this.additives[index];
      if (additive) {
        this.blendAdditiveWeightErrors[index] =
          additive.weight == null ||
          additive.weight === '' as any ||
          isNaN(Number(additive.weight)) ||
          Number(additive.weight) <= 0;
      }
    }
  }

  validateAfterBlendAdditiveWeight(index: number): void {
    const additive = this.afterBlendAdditives[index];
    if (additive) {
      this.afterBlendAdditiveWeightErrors[index] =
        additive.weight == null ||
        additive.weight === '' as any ||
        isNaN(Number(additive.weight)) ||
        Number(additive.weight) <= 0;
    }
  }

  validateLiquidAdditiveVolume(index: number): void {
    const additive = this.liquidAdditives[index];

    this.liquidAdditiveVolumeErrors[index] =
      additive.volume == null ||
      additive.volume === '' as any ||
      isNaN(Number(additive.volume)) ||
      Number(additive.volume) <= 0;
  }

  validateAllBlendAdditives(): void {
    if (this.isBlendTest) {
      this.blendAdditives.forEach((_, index) => {
        this.validateBlendAdditiveWeight(index);
      });
    } else {
      this.additives.forEach((_, index) => {
        this.validateBlendAdditiveWeight(index);
      });
    }
  }

  validateAllAfterBlendAdditives(): void {
    this.afterBlendAdditives.forEach((_, index) => {
      this.validateAfterBlendAdditiveWeight(index);
    });
  }

  validateAllLiquidAdditives(): void {
    this.liquidAdditives.forEach((_, index) => {
      this.validateLiquidAdditiveVolume(index);
    });
  }

  ngOnInit(): void {
    this.validateBaseSelection();
    this.route.paramMap.subscribe(params => {
      const routeProjectId = params.get('projectId');
      if (routeProjectId) {
        this.projectId = Number(routeProjectId);
      }
    });

    // Get user email
    const currentEmail = this.loggedUserService.getCurrentEmail();
    if (currentEmail) {
      this.userEmail = currentEmail;
    }

    // Load project details to get service company info and projectTypeId
    if (this.projectId) {
      this.projectDetailsService.getProjectById(this.projectId).subscribe({
        next: (project) => {
          // Set projectName from API if not already received via navigation state
          if (!this.projectName) {
            this.projectName = project.name ?? null;
          }

          // Set projectTypeId from API if not already set from navigation state
          if (!this.projectTypeIdSetFromNavigation) {
            if (typeof project.projectType === 'number') {
              this.projectTypeId = project.projectType;
            } else if (project.projectType === 'Spacer') {
              this.projectTypeId = 2;
            } else if (project.projectType === 'Slurry') {
              this.projectTypeId = 1;
            } else {
              this.projectTypeId = 2; // Default to Spacer
            }
          }

          // Get service company name if available
          if (project.serviceCompanyId) {
            this.lookupService.getServiceCompanies().subscribe({
              next: (companies) => {
                const company = companies.find(c => c.id === project.serviceCompanyId);
                this.serviceCompanyName = company?.name || '';
              },
              error: (error) => {
                console.error('Error fetching service companies:', error);
              }
            });
          }
        },
        error: (error) => {
          console.error('Error fetching project details:', error);
        }
      });

      // Load slurries for the project
      this.loadProjectSlurries();
    }

    this.subs.add(
      this.blendAdditiveSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(text => {
          if (text.length >= 1) {
            return this.lookupService.searchMaterials(text, 1);
          }
          return of([]);
        })
      ).subscribe(results => {
        // Filter out already selected materials (from blend base and blend additives)
        const selectedIds = this.getSelectedDryMaterialIds();
        this.blendAdditiveOptions = results.filter(
          material => !selectedIds.includes(material.id)
        );
      })
    );

    // For after-blend additives search
    this.subs.add(
      this.afterBlendAdditiveSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(text => {
          if (text.length >= 1) {
            return this.lookupService.searchMaterials(text, 1);
          }
          return of([]);
        })
      ).subscribe(results => {
        // Filter out already selected materials (from blend base, blend additives, and after-blend additives)
        const selectedIds = this.getSelectedDryMaterialIds();
        this.afterBlendAdditiveOptions = results.filter(
          material => !selectedIds.includes(material.id)
        );
      })
    );

    this.subs.add(
      this.liquidSearchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(text => {
          if (text.length >= 1) {
            return this.lookupService.searchMaterials(text, 2);
          }
          return of([]);
        })
      ).subscribe(results => {
        // Filter out already selected liquid materials
        const selectedIds = this.getSelectedLiquidMaterialIds();
        this.liquidAdditiveSearchOptions = results.filter(
          material => !selectedIds.includes(material.id)
        );
      })
    );

    this.loadMaterials();
  }

  loadMaterials(): void {
    // Load Standard Blend Base materials (MaterialTypeID = 7 for SpacerDryBase)
    this.lookupService.getMaterialsByTypeId(7).subscribe({
      next: (materials) => {
        this.standardBlendBaseOptions = materials;
        // Set initial blend base options based on current mode
        if (!this.waterFractionBased) {
          this.blendBaseOptions = this.standardBlendBaseOptions;
        }
      },
      error: (error) => {
      }
    });

    // Load Fraction-Based Blend Base materials (MaterialTypeID = 8 for SpacerDryAdditive - used as blend base in fraction mode)
    this.lookupService.getMaterialsByTypeId(8).subscribe({
      next: (materials) => {
        this.fractionBasedBlendBaseOptions = materials;
        // Set blend base options if in fraction-based mode
        if (this.waterFractionBased) {
          this.blendBaseOptions = this.fractionBasedBlendBaseOptions;
        }
      },
      error: (error) => {
      }
    });

    // Load Standard Liquid Base materials (MaterialTypeID = 9 for SpacerLiquidBase)
    this.lookupService.getMaterialsByTypeId(9).subscribe({
      next: (materials) => {
        this.standardLiquidBaseOptions = materials;
        // Set initial liquid base options based on current mode
        if (!this.waterFractionBased) {
          this.liquidBaseOptions = this.standardLiquidBaseOptions;
        }
      },
      error: (error) => {
      }
    });

    // Load Fraction-Based Liquid Base materials (MaterialTypeID = 10 for SpacerLiquidAdditive - used as liquid base in fraction mode)
    this.lookupService.getMaterialsByTypeId(10).subscribe({
      next: (materials) => {
        this.fractionBasedLiquidBaseOptions = materials;
        // Set liquid base options if in fraction-based mode
        if (this.waterFractionBased) {
          this.liquidBaseOptions = this.fractionBasedLiquidBaseOptions;
        }
      },
      error: (error) => {
      }
    });
  }

  loadProjectSlurries(): void {
    if (!this.projectId) {
      return;
    }

    this.slurryService.getBriefByProjectId(this.projectId).subscribe({
      next: (slurries: SlurryBriefDto[]) => {
        this.projectSlurries = slurries;
      },
      error: (error) => {
        console.error('Error loading project slurries:', error);
        this.projectSlurries = [];
      }
    });
  }

  shouldShowAppendNoteButton(): boolean {
    return false; // Never show for new spacer
  }

  appendNote(): void {
    // TODO: Implement append note functionality
  }

  searchCispro(): void {
    const dialogRef = this.dialog.open(CisProSearchComponent, {
      width: '90vw',
      maxWidth: '1200px',
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe(
      (result: CisProSearchResult | undefined) => {
        if (!result) {
          return;
        }
        this.blendCisproContainer = result.barcodeId;
      }
    );
  }

  removeBlendCispro(): void {
    this.blendCisproContainer = '';
  }

  addAnotherAdditive(type: 'dry' | 'liquid'): void {
    if (type === 'dry') {
      this.additives.push({
        materialId: undefined,
        name: '',
        specificGravity: 0,
        absVolume: 0,
        weight: 0,
        volume: 0,
        labWeight: 0,
        cisproContainer: ''
      });
    } else {
      this.liquidAdditives.push({
        materialId: undefined,
        name: '',
        volume: 0,
        specificGravity: 0,
        weight: 0,
        labWeight: 0,
        cisproContainer: ''
      });
    }
  }

  removeAdditive(index: number, type: 'dry' | 'liquid'): void {
    if (type === 'dry') {
      this.additives.splice(index, 1);
      this.additivesFullPrecisionVolumes.splice(index, 1);
    } else {
      this.liquidAdditives.splice(index, 1);
    }
    // Recalculate after removing
    this.calculate();
  }

  /**
   * ✅ FIXED: Handle both blend test and standard mode additives
   * When additive weight changes in water fraction mode
   */
  onAdditiveWeightChange(index: number): void {
    // In water fraction mode, recalculate volumes and liquid base
    if (this.waterFractionBased && this.waterFractionValue > 0) {
      // Get the correct additive based on isBlendTest flag
      let additive: DryMaterial | undefined;

      if (this.isBlendTest && this.blendAdditives.length > index) {
        additive = this.blendAdditives[index];
      } else if (!this.isBlendTest && this.additives.length > index) {
        additive = this.additives[index];
      }

      // Calculate volume from weight if additive exists with valid data
      if (additive && additive.weight > 0 && additive.specificGravity && additive.specificGravity > 0) {
        const density = this.calculateDensity(additive.specificGravity);
        additive.volume = additive.weight / density;
        this.additivesFullPrecisionVolumes[index] = additive.volume;

        // Now recalculate liquid base to account for new additive volume
        this.updateLiquidBaseFromWaterFraction();
      }
    }

    this.calculate();
  }

  /**
   * ✅ NEW: Handle after-blend additive weight changes
   */
  onAfterBlendAdditiveWeightChange(index: number): void {
    // In water fraction mode, recalculate volumes
    if (this.waterFractionBased && this.waterFractionValue > 0) {
      const additive = this.afterBlendAdditives[index];

      if (additive && additive.weight > 0 && additive.specificGravity && additive.specificGravity > 0) {
        const density = this.calculateDensity(additive.specificGravity);
        additive.volume = additive.weight / density;

        // Recalculate liquid base
        this.updateLiquidBaseFromWaterFraction();
      }
    }

    this.calculate();
  }

  // When liquid additive volume changes
  onLiquidAdditiveVolumeChange(index: number): void {
    this.calculate();
  }

  editMaterial(material: any): void {
    // Open dialog to edit CisPRO containers
    const dialogRef = this.dialog.open(EditComponent, {
      width: '600px',
      data: {
        containers: material.cisproContainer ? material.cisproContainer.split(',').map((c: string) => c.trim()) : []
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Update the material's cisproContainer field
        material.cisproContainer = result.join(', ');
      }
    });
  }

    goBackToDashboard(): void {
        this.router.navigate(['/projects-dashboard']);
    }

    navigateToProject(): void {
        if (!this.projectId) {
            this.goBackToDashboard();
            return;
        }
        this.router.navigate(['/projects-dashboard'], {
            state: { openProjectId: this.projectId, projectInitialTab: 'project' }
        });
    }

  createFinalReport(): void {
    if (!this.projectId) {
      alert('Project ID is required to create final report');
      return;
    }

    if (!this.projectTypeId) {
      alert('Project type is not loaded yet. Please wait and try again.');
      return;
    }

    // Check if we need to load data first
    const needsDataLoad =
      this.projectSlurries.length === 0 ||
      !this.serviceCompanyName ||
      !this.userEmail;

    if (needsDataLoad) {
      // Load required data before opening dialog
      this.loadFinalReportData().then(() => {
        this.openFinalReportDialog();
      }).catch(error => {
        console.error('Error loading final report data:', error);
        alert('Failed to load required data for final report');
      });
    } else {
      // Data already loaded, open dialog directly
      this.openFinalReportDialog();
    }
  }

  private loadFinalReportData(): Promise<void> {
    return new Promise((resolve, reject) => {
      let serviceCompanyLoaded = false;
      let slurriesLoaded = false;
      let userEmailSet = false;

      const checkComplete = () => {
        if (serviceCompanyLoaded && slurriesLoaded && userEmailSet) {
          resolve();
        }
      };

      // Get user email if not already set
      if (!this.userEmail) {
        const currentEmail = this.loggedUserService.getCurrentEmail();
        if (currentEmail) {
          this.userEmail = currentEmail;
        }
        userEmailSet = true;
      } else {
        userEmailSet = true;
      }

      // Load service company name if needed
      if (!this.serviceCompanyName && this.projectId) {
        this.projectDetailsService.getProjectById(this.projectId).subscribe({
          next: (project) => {
            // Get service company name if available
            if (project.serviceCompanyId) {
              this.lookupService.getServiceCompanies().subscribe({
                next: (companies) => {
                  const company = companies.find(c => c.id === project.serviceCompanyId);
                  this.serviceCompanyName = company?.name || '';
                  serviceCompanyLoaded = true;
                  checkComplete();
                },
                error: (error) => {
                  console.error('Error fetching service companies:', error);
                  serviceCompanyLoaded = true;
                  checkComplete();
                }
              });
            } else {
              serviceCompanyLoaded = true;
              checkComplete();
            }
          },
          error: (error) => {
            console.error('Error fetching project details:', error);
            serviceCompanyLoaded = true;
            checkComplete();
          }
        });
      } else {
        serviceCompanyLoaded = true;
      }

      // Load slurries if needed
      if (this.projectSlurries.length === 0 && this.projectId) {
        this.slurryService.getBriefByProjectId(this.projectId).subscribe({
          next: (slurries: SlurryBriefDto[]) => {
            this.projectSlurries = slurries;
            slurriesLoaded = true;
            checkComplete();
          },
          error: (error) => {
            console.error('Error loading project slurries:', error);
            this.projectSlurries = [];
            slurriesLoaded = true;
            checkComplete();
          }
        });
      } else {
        slurriesLoaded = true;
      }

      checkComplete();
    });
  }

  private openFinalReportDialog(): void {
    this.dialog.open(FinalReportDialogComponent, {
      width: 'fit-content',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data: {
        projectId: this.projectId,
        projectTypeId: this.projectTypeId,
        slurries: this.projectSlurries,
        serviceCompanyName: this.serviceCompanyName,
        userEmail: this.userEmail
      }
    });
  }

  createSpacer(): void {
    this.validateBaseSelection();
    this.validateBlendBaseWeight();
    this.validateAllBlendAdditives();
    this.validateAllAfterBlendAdditives();
    this.validateAllLiquidAdditives();

    const hasBlendAdditiveErrors = this.blendAdditiveWeightErrors.some(error => error);
    const hasAfterBlendAdditiveErrors = this.afterBlendAdditiveWeightErrors.some(error => error);
    const hasLiquidAdditiveErrors = this.liquidAdditiveVolumeErrors.some(error => error);

    if (
      this.blendBaseError ||
      this.liquidBaseError ||
      this.blendBaseWeightError ||
      hasBlendAdditiveErrors ||
      hasAfterBlendAdditiveErrors ||
      hasLiquidAdditiveErrors
    ) {
      return;
    }

    const materials: CreateSpacerMaterialDto[] = [];

    // Blend Base
    if (this.blendBase.materialId) {
      materials.push({
        materialId: this.blendBase.materialId,
        materialName: this.blendBase.name,
        weight: this.blendBase.weight || 0,
        absoluteVolume: this.blendBase.absVolume,
        volume: this.blendBase.volume,
        labWeight: this.blendBase.labWeight,
        specificGravity: this.blendBase.specificGravity,
        materialTypeId: this.waterFractionBased ? 8 : 7,
        cisProContainer: this.blendBase.cisproContainer
      });
    }

    // Dry Additives (Standard Mode)
    this.additives.forEach(additive => {
      if (additive.materialId) {
        materials.push({
          materialId: additive.materialId,
          materialName: additive.name,
          weight: additive.weight || 0,
          absoluteVolume: additive.absVolume,
          volume: additive.volume,
          labWeight: additive.labWeight,
          specificGravity: additive.specificGravity,
          materialTypeId: 8,
          cisProContainer: additive.cisproContainer
        });
      }
    });

    // Blend Additives (Blend Test Mode - materials added before blend)
    this.blendAdditives.forEach(additive => {
      if (additive.materialId) {
        materials.push({
          materialId: additive.materialId,
          materialName: additive.name,
          weight: additive.weight || 0,
          absoluteVolume: additive.absVolume,
          volume: additive.volume,
          labWeight: additive.labWeight,
          specificGravity: additive.specificGravity,
          materialTypeId: 8,
          cisProContainer: additive.cisproContainer,
          afterBlend: false
        });
      }
    });

    // After-Blend Additives (Blend Test Mode - materials added after blend)
    this.afterBlendAdditives.forEach(additive => {
      if (additive.materialId) {
        materials.push({
          materialId: additive.materialId,
          materialName: additive.name,
          weight: additive.weight || 0,
          absoluteVolume: additive.absVolume,
          volume: additive.volume,
          labWeight: additive.labWeight,
          specificGravity: additive.specificGravity,
          materialTypeId: 8,
          cisProContainer: additive.cisproContainer,
          afterBlend: true
        });
      }
    });

    // Liquid Base
    if (this.rigFreshWater.materialId) {
      materials.push({
        materialId: this.rigFreshWater.materialId,
        materialName: this.rigFreshWater.name,
        weight: this.rigFreshWater.weight || 0,
        volume: this.rigFreshWater.volume,
        labWeight: this.rigFreshWater.labWeight,
        specificGravity: this.rigFreshWater.specificGravity,
        materialTypeId: this.waterFractionBased ? 10 : 9,
        cisProContainer: this.rigFreshWater.cisproContainer
      });
    }

    // Liquid Additives
    this.liquidAdditives.forEach(additive => {
      if (additive.materialId) {
        materials.push({
          materialId: additive.materialId,
          materialName: additive.name,
          weight: additive.weight || 0,
          volume: additive.volume,
          labWeight: additive.labWeight,
          specificGravity: additive.specificGravity,
          materialTypeId: 10,
          cisProContainer: additive.cisproContainer
        });
      }
    });

    if (!this.projectId || this.projectId <= 0) {
      alert('Project ID is invalid');
      return;
    }

    const request: CreateSpacerRequestDto = {
      projectId: this.projectId,
      labVolume: this.labVolume,
      totalDensity: this.totalDensity,
      notes: this.notes,
      waterFractionBased: this.waterFractionBased,
      waterFraction: this.waterFractionValue,
      viscosifier: this.viscosifierValue,
      isBlendTest: this.isBlendTest,
      preferredBlendCisPROContainerID: this.blendCisproContainer,
      materials: materials,
      tests: null
    };

    this.spacerService.createSpacer(request).subscribe({
      next: (response) => {
        // Update redesign reason if redesign data exists
        if (response.slurryId && this.redesignData?.isRedesign) {
          this.updateRedesignReason(response.slurryId);
        }
        
        // Navigate to spacer details page directly
        if (response.slurryId && this.projectId) {
          this.router.navigate(
            ['/projects-dashboard/spacer', this.projectId, response.slurryId],
            {
              state: {
                projectId: this.projectId,
                projectName: this.projectName,
                blendTest: this.isBlendTest,
                slurry: response
              }
            }
          );
        }
      },
      error: (error) => {
        alert(JSON.stringify(error.error, null, 2));
      }
    });
  }

  private updateRedesignReason(slurryId: number): void {
    const redesignRequest: UpdateSpacerRedesignReasonRequestDto = {
      slurryId: slurryId,
      isRedesign: this.redesignData.isRedesign,
      reasonType: this.redesignData.reasonTypeId || null,
      comments: this.redesignData.comments || null,
      parentSlurryId: null, // Always NULL for new spacer creation (not a duplicate)
      complianceTests: this.redesignData.complianceTestIds || []
    };

    this.spacerService.updateRedesignReason(slurryId, redesignRequest).subscribe({
      next: () => {
        console.log('Redesign reason updated successfully for new spacer with ParentSlurryId=NULL');
      },
      error: (error) => {
        console.error('Error updating redesign reason:', error);
        // Continue navigation even if redesign reason update fails
      }
    });
  }

  // When a blend base material is selected from dropdown
  onBlendBaseMaterialChange(event: any): void {
    const selectedMaterial = this.blendBaseOptions.find(m => m.name === this.blendBase.name);
    if (selectedMaterial) {
      this.blendBase.materialId = selectedMaterial.id;
      this.blendBase.specificGravity = selectedMaterial.specificGravity || 0;

      if (!this.waterFractionBased) {
        this.blendBase.weight = null as any;
        this.blendBase.volume = 0;
        this.blendBase.labWeight = 0;
        this.totalDensity = 0;
        this.blendBaseFullPrecisionWeight = 0;
      }

      // Calculate absVolume from specificGravity with full precision
      if (this.blendBase.specificGravity > 0) {
        this.blendBaseFullPrecisionAbsVolume = this.calculateAbsoluteVolume(this.blendBase.specificGravity);
        this.blendBase.absVolume = Math.round(this.blendBaseFullPrecisionAbsVolume * 10000) / 10000;
      } else if (selectedMaterial.absoluteVolume && selectedMaterial.absoluteVolume > 0) {
        // Fallback to database value if SG not available
        this.blendBaseFullPrecisionAbsVolume = selectedMaterial.absoluteVolume;
        this.blendBase.absVolume = Math.round(selectedMaterial.absoluteVolume * 10000) / 10000;
      } else {
        this.blendBase.absVolume = 0;
        this.blendBaseFullPrecisionAbsVolume = 0;
      }

      // If in water fraction mode, recalculate everything
      if (this.waterFractionBased) {
        this.updateBlendBaseWeightFromWaterFraction();
      } else {
        // In standard mode, trigger calculation if weight is already entered
        if (this.blendBase.weight && this.blendBase.weight > 0) {
          this.calculate();
        }
      }
    }
    this.validateBaseSelection();
  }

  // When a liquid base material is selected from dropdown
  onLiquidMaterialChange(event: any): void {
    const selectedMaterial = this.liquidBaseOptions.find(m => m.name === this.rigFreshWater.name);
    if (selectedMaterial) {
      this.rigFreshWater.materialId = selectedMaterial.id;
      // Store FULL PRECISION specific gravity for calculations
      this.liquidBaseFullPrecisionSpecificGravity = selectedMaterial.specificGravity || 0;
      // Round only for display
      this.rigFreshWater.specificGravity = Math.round(this.liquidBaseFullPrecisionSpecificGravity * 1000) / 1000;

      // Only calculate volume, weight, lab weight if conditions are met
      if (this.waterFractionBased) {
        // In water fraction mode, only calculate if water fraction has been entered
        if (this.waterFractionValue > 0) {
          this.updateLiquidBaseFromWaterFraction();
        }
      } else {
        // In standard mode, calculate liquid volume as: 42 gallons - dry material volumes
        const dryVolume = this.blendBaseFullPrecisionVolume || 0;
        this.rigFreshWater.volume = Math.round((this.SPACER_TOTAL_VOLUME - dryVolume) * 1000) / 1000;
        // Trigger calculation to compute weight and lab weight
        this.calculate();
      }
    }
    this.validateBaseSelection();
  }

  // When weight input changes for blend base (text input)
  onWeightInputChange(event: any): void {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      this.blendBase.weight = value;
      this.onWeightChange();
    }
  }

  // When weight changes for dry materials
  onWeightChange(): void {
    // If in standard mode and liquid base is selected, it should auto-calculate after dry materials
    if (!this.waterFractionBased && this.rigFreshWater.materialId) {
      // Mark that we need to recalculate liquid base after blend base volume is calculated
      this.calculateMaterialsAndUpdateLiquid();
    } else {
      this.calculate();
    }
  }

  // Calculate materials and then update liquid base volume in standard mode
  private calculateMaterialsAndUpdateLiquid(): void {
    this.calculate();
  }

  // When water fraction based checkbox changes
  onWaterFractionBasedChange(): void {
    if (this.waterFractionBased) {
      this.waterFractionError = false;
      this.viscosifierError = false;
      this.waterFractionTouched = false;
      this.viscosifierTouched = false;

      // Switch to fraction-based material sets
      this.blendBaseOptions = this.fractionBasedBlendBaseOptions;
      this.liquidBaseOptions = this.fractionBasedLiquidBaseOptions;

      // Clear current selections as they may not be valid in new mode
      this.clearMaterialSelections();
      this.validateBaseSelection();
      
      // Clear blend CisPRO container when blend test is true
      if (this.isBlendTest) {
        this.blendCisproContainer = '';
      }
      
      // When enabled, update blend base weight based on formula
      this.updateBlendBaseWeightFromWaterFraction();
    } else {
      this.waterFractionError = false;
      this.viscosifierError = false;
      this.waterFractionTouched = false;
      this.viscosifierTouched = false;

      // Switch back to standard material sets
      this.blendBaseOptions = this.standardBlendBaseOptions;
      this.liquidBaseOptions = this.standardLiquidBaseOptions;

      // Clear current selections
      this.clearMaterialSelections();
      this.validateBaseSelection();
      
      // Clear blend CisPRO container when blend test is true
      if (this.isBlendTest) {
        this.blendCisproContainer = '';
      }
      
      // When disabled, reset to manual mode
      this.waterFractionValue = 0;
      this.viscosifierValue = 0;
    }
  }

  // Clear material selections when switching modes
  private clearMaterialSelections(): void {
    // Reset blend base
    this.blendBase = {
      materialId: undefined,
      name: '',
      specificGravity: 0,
      absVolume: 0,
      weight: null as any,
      volume: 0,
      labWeight: 0,
      cisproContainer: ''
    };

    // Reset liquid base
    this.rigFreshWater = {
      materialId: undefined,
      name: '',
      volume: 0,
      specificGravity: 0,
      weight: 0,
      labWeight: 0,
      cisproContainer: ''
    };

    // Clear all additive arrays
    this.additives = [];
    this.blendAdditives = [];
    this.afterBlendAdditives = [];
    this.liquidAdditives = [];
    this.additivesFullPrecisionVolumes = [];

    // Clear error arrays
    this.blendAdditiveWeightErrors = [];
    this.afterBlendAdditiveWeightErrors = [];
    this.liquidAdditiveVolumeErrors = [];

    // Reset totals
    this.totalDensity = 0;
    this.totalDensityFullPrecision = 0;
    this.validateBaseSelection();
  }

  // When water fraction value changes
  onWaterFractionChange(): void {
    if (this.waterFractionBased) {
      this.waterFractionTouched = true;
      this.waterFractionError =
        this.waterFractionValue === null ||
        this.waterFractionValue === undefined ||
        this.waterFractionValue === 0;

      if (this.waterFractionValue === 0 || this.waterFractionValue === null || this.waterFractionValue === undefined) {
        // Reset all calculated values when water fraction is cleared
        this.resetCalculatedValues();
      } else {
        // Scenario 2: Auto-calculate blend base weight if material is selected
        if (this.blendBase.materialId && this.viscosifierValue > 0) {
          this.blendBase.weight = this.viscosifierValue * (this.waterFractionValue / 100);
        }
        this.calculate();
      }
    }
  }

  // When viscosifier value changes
  onViscosifierChange(): void {
    if (this.waterFractionBased) {
      this.viscosifierTouched = true;
      this.viscosifierError =
        this.viscosifierValue === null ||
        this.viscosifierValue === undefined ||
        this.viscosifierValue === 0;

      if (this.viscosifierValue === 0 || this.viscosifierValue === null || this.viscosifierValue === undefined) {
        // Reset all calculated values when viscosifier is cleared
        this.resetCalculatedValues();
      } else {
        // Scenario 2: Auto-calculate blend base weight if material is selected
        if (this.blendBase.materialId && this.waterFractionValue > 0) {
          this.blendBase.weight = this.viscosifierValue * (this.waterFractionValue / 100);
        }
        this.calculate();
      }
    }
  }

  // Reset all calculated values
  private resetCalculatedValues(): void {
    this.blendBase.weight = 0;
    this.blendBase.volume = 0;
    this.blendBase.labWeight = 0;
    this.rigFreshWater.volume = 0;
    this.rigFreshWater.weight = 0;
    this.rigFreshWater.labWeight = 0;
    this.totalDensity = 0;
    this.totalDensityFullPrecision = 0;
    this.blendBaseFullPrecisionWeight = 0;
    this.blendBaseFullPrecisionVolume = 0;
    this.liquidBaseFullPrecisionWeight = 0;
    this.liquidBaseFullPrecisionVolume = 0;
    this.additivesFullPrecisionVolumes = [];
  }

  // Calculate blend base weight from water fraction and viscosifier
  private updateBlendBaseWeightFromWaterFraction(): void {
    if (this.waterFractionValue > 0 && this.viscosifierValue > 0) {
      // Do not calculate weight until a blend base material is selected
      if (!this.blendBase.materialId) {
        this.blendBase.weight = null as any;
        this.blendBase.volume = 0;
        this.blendBase.labWeight = 0;
        return;
      }

      // Formula: Blend Base Weight = Viscosifier × (Water Fraction / 100)
      this.blendBaseFullPrecisionWeight = this.viscosifierValue * (this.waterFractionValue / 100);
      this.blendBase.weight = Math.round(this.blendBaseFullPrecisionWeight * 1000) / 1000;

      // Calculate volume using full-precision density
      if (this.blendBase.specificGravity > 0) {
        const density = this.calculateDensity(this.blendBase.specificGravity);
        this.blendBaseFullPrecisionVolume = this.blendBaseFullPrecisionWeight / density;
        this.blendBase.volume = Math.round(this.blendBaseFullPrecisionVolume * 1000) / 1000;

        // Update displayed absVolume
        this.blendBase.absVolume = this.calculateAbsoluteVolume(this.blendBase.specificGravity);

        // Trigger liquid base recalculation
        this.updateLiquidBaseFromWaterFraction();

        if (!this.rigFreshWater.materialId) {
          this.calculateLabWeightsAndTotalDensity();
        }
      }
    } else if (this.waterFractionValue === 0 || this.viscosifierValue === 0) {
      // Reset when either value is 0
      this.resetCalculatedValues();
    }
  }

  // Calculate liquid base properties from water fraction
  private updateLiquidBaseFromWaterFraction(): void {
    if (!this.waterFractionBased || this.waterFractionValue === 0) {
      return;
    }

    if (!this.rigFreshWater.materialId || (!this.liquidBaseFullPrecisionSpecificGravity && !this.rigFreshWater.specificGravity)) {
      return;
    }

    // Use full precision specific gravity if available, otherwise use displayed value
    const specificGravity = this.liquidBaseFullPrecisionSpecificGravity > 0 ?
      this.liquidBaseFullPrecisionSpecificGravity : this.rigFreshWater.specificGravity;

    // Formula for Water Fraction Based Mode:
    // Liquid Base Volume = 42 × (WaterFraction / 100)
    this.liquidBaseFullPrecisionVolume = this.SPACER_TOTAL_VOLUME * (this.waterFractionValue / 100);
    this.rigFreshWater.volume = Math.round(this.liquidBaseFullPrecisionVolume * 1000) / 1000;

    // Calculate liquid base weight: Weight = Volume × Specific Gravity × Water Density (ALL FULL PRECISION)
    this.liquidBaseFullPrecisionWeight =
      this.liquidBaseFullPrecisionVolume *
      specificGravity *
      this.WATER_DENSITY;
    this.rigFreshWater.weight = Math.round(this.liquidBaseFullPrecisionWeight * 1000) / 1000;

    // Calculate lab weights and total density after all weights/volumes are calculated
    this.calculateLabWeightsAndTotalDensity();
  }

  // Calculate lab weights and total density (second pass after weights/volumes are calculated)
  private calculateLabWeightsAndTotalDensity(): void {
    // First calculate total weight and total density using full precision
    this.calculateTotalDensity();

    // Calculate total weight using FULL PRECISION values
    let totalWeight = 0;
    if (this.blendBaseFullPrecisionWeight > 0) {
      totalWeight += this.blendBaseFullPrecisionWeight;
    } else if (this.blendBase.weight) {
      totalWeight += this.blendBase.weight;
    }

    // ✅ Include ALL additive arrays
    this.additives.forEach(additive => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    this.blendAdditives.forEach(additive => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    this.afterBlendAdditives.forEach(additive => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    if (this.liquidBaseFullPrecisionWeight > 0) {
      totalWeight += this.liquidBaseFullPrecisionWeight;
    } else if (this.rigFreshWater.weight) {
      totalWeight += this.rigFreshWater.weight;
    }

    this.liquidAdditives.forEach(liquid => {
      if (liquid.weight) {
        totalWeight += liquid.weight;
      }
    });

    // Use FULL PRECISION total density for accurate lab weight calculation
    const totalDensity = this.totalDensityFullPrecision > 0 ? this.totalDensityFullPrecision : this.totalDensity;

    if (totalWeight > 0 && totalDensity > 0) {
      if (this.blendBase.materialId && (this.blendBaseFullPrecisionWeight > 0 || this.blendBase.weight > 0)) {
        const weightToUse = this.blendBaseFullPrecisionWeight > 0 ? this.blendBaseFullPrecisionWeight : this.blendBase.weight;
        this.blendBase.labWeight = Math.round((totalDensity * this.labVolume * weightToUse * this.LB_PER_GAL_IN_G_PER_ML) / totalWeight * 100) / 100;
      } else {
        this.blendBase.labWeight = 0;
      }

      if (this.liquidBaseFullPrecisionWeight > 0 || this.rigFreshWater.weight > 0) {
        const weightToUse = this.liquidBaseFullPrecisionWeight > 0 ? this.liquidBaseFullPrecisionWeight : this.rigFreshWater.weight;
        this.rigFreshWater.labWeight = Math.round((totalDensity * this.labVolume * weightToUse * this.LB_PER_GAL_IN_G_PER_ML) / totalWeight * 100) / 100;
      }
    }
  }

  // Calculate total density
  private calculateTotalDensity(): void {
    // Use FULL PRECISION weights for total weight calculation
    let totalWeight = 0;

    // Add blend base weight (use full precision if available)
    if (this.blendBaseFullPrecisionWeight > 0) {
      totalWeight += this.blendBaseFullPrecisionWeight;
    } else if (this.blendBase.weight) {
      totalWeight += this.blendBase.weight;
    }

    // ✅ Add ALL dry additive weights from ALL arrays
    this.additives.forEach((additive, index) => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    this.blendAdditives.forEach((additive, index) => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    this.afterBlendAdditives.forEach((additive, index) => {
      if (additive.weight) {
        totalWeight += additive.weight;
      }
    });

    // Add liquid base weight (use full precision if available)
    if (this.liquidBaseFullPrecisionWeight > 0) {
      totalWeight += this.liquidBaseFullPrecisionWeight;
    } else if (this.rigFreshWater.weight) {
      totalWeight += this.rigFreshWater.weight;
    }

    // Add liquid additive weights
    this.liquidAdditives.forEach((liquid, index) => {
      if (liquid.weight) {
        totalWeight += liquid.weight;
      }
    });

    // In Water Fraction Based mode: Total Density = Total Weight / Total Volume of materials (not 42 gal)
    // In Standard mode: Total Density = Total Weight / 42 gal
    if (this.waterFractionBased) {
      // Calculate total volume of materials only using FULL PRECISION volumes
      let totalMaterialVolume = 0;

      // Use full precision volume for blend base
      if (this.blendBaseFullPrecisionVolume > 0) {
        totalMaterialVolume += this.blendBaseFullPrecisionVolume;
      }

      // ✅ Add volumes from ALL additive arrays
      this.additives.forEach((additive, index) => {
        if (additive.volume) {
          // Use full precision if available, otherwise rounded value
          totalMaterialVolume += this.additivesFullPrecisionVolumes[index] || additive.volume;
        }
      });

      this.blendAdditives.forEach(additive => {
        if (additive.volume) {
          totalMaterialVolume += additive.volume;
        }
      });

      this.afterBlendAdditives.forEach(additive => {
        if (additive.volume) {
          totalMaterialVolume += additive.volume;
        }
      });

      // Use full precision volume for liquid base
      if (this.liquidBaseFullPrecisionVolume > 0) {
        totalMaterialVolume += this.liquidBaseFullPrecisionVolume;
      }

      this.liquidAdditives.forEach(liquid => {
        if (liquid.volume) {
          totalMaterialVolume += liquid.volume;
        }
      });

      if (totalMaterialVolume > 0) {
        // Store FULL PRECISION density for calculations
        this.totalDensityFullPrecision = totalWeight / totalMaterialVolume;
        // Round to 2 decimals for display only
        this.totalDensity = Math.round(this.totalDensityFullPrecision * 100) / 100;
      }
    } else {
      // Standard mode: use 42 gallon total volume
      this.totalDensityFullPrecision = totalWeight / this.SPACER_TOTAL_VOLUME;
      this.totalDensity = Math.round(this.totalDensityFullPrecision * 100) / 100;
    }
  }

  onBlendBaseAbsVolumeChange(): void {
    this.blendBaseFullPrecisionAbsVolume = this.blendBase.absVolume || 0;
    this.calculate();
  }

  onAdditiveAbsVolumeChange(index: number): void {
    this.calculate();
  }

  onLiquidBaseSpecificGravityChange(): void {
    this.liquidBaseFullPrecisionSpecificGravity = this.rigFreshWater.specificGravity || 0;
    this.calculate();
  }

  onLiquidAdditiveSpecificGravityChange(index: number): void {
    this.calculate();
  }

  // ── Client-side calculation (matching spacer-details logic) ───
  onMaterialValueChange(): void { 
    this.calculate(); 
  }

  /**
   * ✅ MAIN CALCULATION METHOD - Matches WPF logic exactly
   * This method calculates all material volumes, weights, and lab weights
   */
  private calculate(): void {
    if (!this.labVolume || this.labVolume <= 0) return;

    // Step 1: Dry base weight and volume calculation with full precision
    // Scenario 2A: Weight = Viscosifier × (WaterFraction ÷ 100)
    if (this.waterFractionBased && this.viscosifierValue != null && this.waterFractionValue != null && this.blendBase.materialId != null) {
      this.blendBase.weight = Math.round(this.viscosifierValue * (this.waterFractionValue / 100) * 1000) / 1000;
    }

    if (this.blendBase.materialId != null && this.blendBase.specificGravity > 0) {
      // Calculate using full-precision density (matches C# DryMaterial.Volume)
      const density = this.calculateDensity(this.blendBase.specificGravity);
      this.blendBase.volume = density > 0 ? this.blendBase.weight / density : 0;
      // Update displayed absVolume from full-precision calculation
      this.blendBase.absVolume = Math.round(this.calculateAbsoluteVolume(this.blendBase.specificGravity) * 10000) / 10000;
    }

    // ✅ Calculate volumes for ALL additive arrays using full-precision density
    for (const m of this.additives) {
      if (m.specificGravity > 0) {
        const density = this.calculateDensity(m.specificGravity);
        m.volume = density > 0 ? m.weight / density : 0;
        m.absVolume = Math.round(this.calculateAbsoluteVolume(m.specificGravity) * 10000) / 10000;
      }
    }

    for (const m of this.blendAdditives) {
      if (m.specificGravity > 0) {
        const density = this.calculateDensity(m.specificGravity);
        m.volume = density > 0 ? m.weight / density : 0;
        m.absVolume = Math.round(this.calculateAbsoluteVolume(m.specificGravity) * 10000) / 10000;
      }
    }

    for (const m of this.afterBlendAdditives) {
      if (m.specificGravity > 0) {
        const density = this.calculateDensity(m.specificGravity);
        m.volume = density > 0 ? m.weight / density : 0;
        m.absVolume = Math.round(this.calculateAbsoluteVolume(m.specificGravity) * 10000) / 10000;
      }
    }

    // Step 2: Liquid additive weights (matches C# LiquidMaterial.Weight)
    for (const m of this.liquidAdditives) {
      m.weight = m.volume * m.specificGravity * this.WATER_DENSITY;
    }

    // Step 3: Liquid base volume calculation
    const dryTotalVolume =
      (this.blendBase.materialId != null ? this.blendBase.volume : 0) +
      this.additives.reduce((s, m) => s + m.volume, 0) +
      this.blendAdditives.reduce((s, m) => s + m.volume, 0) +
      this.afterBlendAdditives.reduce((s, m) => s + m.volume, 0);
    const liquidAdditiveVolume = this.liquidAdditives.reduce((s, m) => s + m.volume, 0);
    const liquidAdditiveWeight = this.liquidAdditives.reduce((s, m) => s + m.weight, 0);

    let liquidBaseVolume = 0;
    if (this.rigFreshWater.materialId != null) {
      if (this.waterFractionBased && this.waterFractionValue != null) {
        // Scenario 2B: Volume = 42 × (WaterFraction ÷ 100)
        liquidBaseVolume = Math.max(0, (this.waterFractionValue / 100) * this.SPACER_TOTAL_VOLUME);
      } else {
        // Scenario 1C: Liquid Base Volume = 42 − (Dry Base Vol + Dry Additives Vol + Liquid Additives Vol)
        // Matches C# DefaultSpacerCalculationStrategy.Recalculate
        liquidBaseVolume = Math.max(0, this.SPACER_TOTAL_VOLUME - dryTotalVolume - liquidAdditiveVolume);
      }
      this.rigFreshWater.volume = liquidBaseVolume;
      this.rigFreshWater.weight = liquidBaseVolume * this.rigFreshWater.specificGravity * this.WATER_DENSITY;
    }

    // Step 4: Totals and density (matches C# SpacerMaterials.TotalDensity)
    const totalDryWeight =
      (this.blendBase.materialId != null ? this.blendBase.weight : 0) +
      this.additives.reduce((s, m) => s + m.weight, 0) +
      this.blendAdditives.reduce((s, m) => s + m.weight, 0) +
      this.afterBlendAdditives.reduce((s, m) => s + m.weight, 0);
    const liquidBaseWeight = this.rigFreshWater.materialId != null ? this.rigFreshWater.weight : 0;
    const totalWeight = totalDryWeight + liquidBaseWeight + liquidAdditiveWeight;
    const totalVolume = dryTotalVolume +
      (this.rigFreshWater.materialId != null ? liquidBaseVolume : 0) + liquidAdditiveVolume;

    // Calculate total density with full precision for intermediate calculations
    const totalDensityFullPrecision = totalVolume > 0 ? totalWeight / totalVolume : 0;
    this.totalDensityFullPrecision = totalDensityFullPrecision;
    this.totalDensity = totalDensityFullPrecision > 0 ? Math.round(totalDensityFullPrecision * 100) / 100 : 0;

    // Step 5: Lab weights using full precision density (matches C# Material.LabWeight)
    if (totalWeight > 0 && totalDensityFullPrecision > 0) {
      const factor = totalDensityFullPrecision * this.labVolume * this.LB_PER_GAL_IN_G_PER_ML / totalWeight;
      if (this.blendBase.materialId != null) {
        this.blendBase.labWeight = Math.round(this.blendBase.weight * factor * 100) / 100;
      }
      // ✅ Calculate lab weights for ALL additive arrays
      for (const m of this.additives) {
        m.labWeight = Math.round(m.weight * factor * 100) / 100;
      }
      for (const m of this.blendAdditives) {
        m.labWeight = Math.round(m.weight * factor * 100) / 100;
      }
      for (const m of this.afterBlendAdditives) {
        m.labWeight = Math.round(m.weight * factor * 100) / 100;
      }
      if (this.rigFreshWater.materialId != null) {
        this.rigFreshWater.labWeight = Math.round(this.rigFreshWater.weight * factor * 100) / 100;
      }
      for (const m of this.liquidAdditives) {
        m.labWeight = Math.round(m.weight * factor * 100) / 100;
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ── Blend Additives autocomplete ────────────────────────────
  showAddBlendAdditiveSearch(): void {
    this.showBlendAdditiveSearch = true;
    this.blendAdditiveSearchText = '';
    this.blendAdditiveOptions = [];
  }

  onBlendAdditiveSearch(text: string): void { 
    this.blendAdditiveSubject.next(text); 
  }

  selectBlendAdditive(material: MaterialSearchResult): void {
    // Check if material is already selected (blend base or additives)
    const selectedIds = this.getSelectedDryMaterialIds();
    if (selectedIds.includes(material.id)) {
      return;
    }

    const newMaterial = {
      materialId: material.id,
      name: material.name ?? '',
      specificGravity: material.specificGravity ?? 0,
      absVolume: Math.round((material.absoluteVolume ?? 0) * 10000) / 10000,
      weight: 0,
      volume: 0,
      labWeight: 0,
      cisproContainer: ''
    };

    if (this.isBlendTest) {
      // In blend test mode, add to blendAdditives array
      this.blendAdditives = [...this.blendAdditives, newMaterial];
    } else {
      // In standard mode, add to additives array
      this.additives = [...this.additives, newMaterial];
    }

    this.blendAdditiveWeightErrors.push(true);
    this.showBlendAdditiveSearch = false;
    this.blendAdditiveSearchText = '';
    this.blendAdditiveOptions = [];
  }

  onBlendAdditiveBlur(): void {
    setTimeout(() => { 
      this.showBlendAdditiveSearch = false; 
      this.blendAdditiveSearchText = ''; 
      this.blendAdditiveOptions = []; 
    }, 150);
  }

  removeBlendAdditive(item: DryMaterial): void {
    const index = this.additives.indexOf(item);
    if (index > -1) {
      this.blendAdditiveWeightErrors.splice(index, 1);
    }
    this.additives = this.additives.filter(m => m !== item);

    // Also remove from blendAdditives array if it exists there
    const blendIndex = this.blendAdditives.indexOf(item);
    if (blendIndex > -1) {
      this.blendAdditiveWeightErrors.splice(blendIndex, 1);
      this.blendAdditives = this.blendAdditives.filter(m => m !== item);
    }

    this.calculate();
  }

  // ── After-Blend Additives autocomplete ──────────────────────
  showAddAfterBlendAdditiveSearch(): void {
    this.showAfterBlendAdditiveSearch = true;
    this.afterBlendAdditiveSearchText = '';
    this.afterBlendAdditiveOptions = [];
  }

  onAfterBlendAdditiveSearch(text: string): void {
    this.afterBlendAdditiveSubject.next(text);
  }

  selectAfterBlendAdditive(material: MaterialSearchResult): void {
    // Check if material is already selected
    const selectedIds = this.getSelectedDryMaterialIds();
    if (selectedIds.includes(material.id)) {
      return;
    }

    this.afterBlendAdditives = [...this.afterBlendAdditives, {
      materialId: material.id,
      name: material.name ?? '',
      specificGravity: material.specificGravity ?? 0,
      absVolume: Math.round((material.absoluteVolume ?? 0) * 10000) / 10000,
      weight: 0,
      volume: 0,
      labWeight: 0,
      cisproContainer: ''
    }];
    this.afterBlendAdditiveWeightErrors.push(true);
    this.showAfterBlendAdditiveSearch = false;
    this.afterBlendAdditiveSearchText = '';
    this.afterBlendAdditiveOptions = [];
  }

  onAfterBlendAdditiveBlur(): void {
    setTimeout(() => {
      this.showAfterBlendAdditiveSearch = false;
      this.afterBlendAdditiveSearchText = '';
      this.afterBlendAdditiveOptions = [];
    }, 150);
  }

  removeAfterBlendAdditive(item: DryMaterial): void {
    const index = this.afterBlendAdditives.indexOf(item);
    if (index > -1) {
      this.afterBlendAdditiveWeightErrors.splice(index, 1);
    }
    this.afterBlendAdditives = this.afterBlendAdditives.filter(m => m !== item);
    this.calculate();
  }

  // ── Liquid additive autocomplete ────────────────────────────
  showAddLiquidAdditiveSearch(): void {
    this.showLiquidAdditiveSearch = true;
    this.liquidAdditiveSearchText = '';
    this.liquidAdditiveSearchOptions = [];
  }

  onLiquidAdditiveSearch(text: string): void { 
    this.liquidSearchSubject.next(text); 
  }

  selectLiquidAdditive(material: MaterialSearchResult): void {
    // Check if material is already selected (liquid base or liquid additives)
    const selectedIds = this.getSelectedLiquidMaterialIds();
    if (selectedIds.includes(material.id)) {
      return;
    }

    this.liquidAdditives = [...this.liquidAdditives, {
      materialId: material.id,
      name: material.name ?? '',
      volume: 0,
      specificGravity: material.specificGravity ?? 0,
      weight: 0,
      labWeight: 0,
      cisproContainer: ''
    }];
    this.liquidAdditiveVolumeErrors.push(true);
    this.showLiquidAdditiveSearch = false;
    this.liquidAdditiveSearchText = '';
    this.liquidAdditiveSearchOptions = [];
  }

  onLiquidAdditiveBlur(): void {
    setTimeout(() => { 
      this.showLiquidAdditiveSearch = false; 
      this.liquidAdditiveSearchText = ''; 
      this.liquidAdditiveSearchOptions = []; 
    }, 150);
  }

  removeLiquidAdditive(item: LiquidMaterial): void {
    const index = this.liquidAdditives.indexOf(item);
    if (index > -1) {
      this.liquidAdditiveVolumeErrors.splice(index, 1);
    }
    this.liquidAdditives = this.liquidAdditives.filter(m => m !== item);
    this.calculate();
  }

  onDryBaseSelected(id: number | null): void {
    const material = this.blendBaseOptions.find(m => m.id === id);
    if (material) {
      // Calculate absVolume from specificGravity with full precision
      let absVolume = 0;
      if (material.specificGravity && material.specificGravity > 0) {
        absVolume = this.calculateAbsoluteVolume(material.specificGravity);
      } else if (material.absoluteVolume && material.absoluteVolume > 0) {
        absVolume = material.absoluteVolume;
      }

      this.blendBase = {
        materialId: material.id,
        name: material.name ?? '',
        specificGravity: material.specificGravity ?? 0,
        absVolume: absVolume,
        weight: 0,
        volume: 0,
        labWeight: 0,
        cisproContainer: ''
      };

      // Scenario 2: If water fraction based, auto-calculate weight
      if (this.waterFractionBased && this.waterFractionValue > 0 && this.viscosifierValue > 0) {
        // Auto-calculate weight: Weight = Viscosifier × (WaterFraction / 100)
        this.blendBase.weight = Math.round(this.viscosifierValue * (this.waterFractionValue / 100) * 10000) / 10000;
      }
    } else {
      this.blendBase = {
        materialId: undefined,
        name: '',
        specificGravity: 0,
        absVolume: 0,
        weight: 0,
        volume: 0,
        labWeight: 0,
        cisproContainer: ''
      };
    }
    this.validateBaseSelection();
    this.validateBlendBaseWeight();
    this.calculate();
  }

  onLiquidBaseSelected(id: number | null): void {
    const material = this.liquidBaseOptions.find(m => m.id === id);
    if (material) {
      this.rigFreshWater = {
        materialId: material.id,
        name: material.name ?? '',
        specificGravity: material.specificGravity ?? 0,
        volume: 0,
        weight: 0,
        labWeight: 0,
        cisproContainer: ''
      };
    } else {
      this.rigFreshWater = {
        materialId: undefined,
        name: '',
        volume: 0,
        specificGravity: 0,
        weight: 0,
        labWeight: 0,
        cisproContainer: ''
      };
    }
    this.validateBaseSelection();
    this.calculate();
  }

  // Helper method to get all selected dry material IDs (blend base + all additives)
  private getSelectedDryMaterialIds(): number[] {
    const ids: number[] = [];

    // Include blend base if selected
    if (this.blendBase.materialId != null) {
      ids.push(this.blendBase.materialId);
    }

    // Include all additives (standard mode)
    this.additives.forEach(additive => {
      if (additive.materialId != null) {
        ids.push(additive.materialId);
      }
    });

    // Include blend additives (blend test mode)
    this.blendAdditives.forEach(additive => {
      if (additive.materialId != null) {
        ids.push(additive.materialId);
      }
    });

    // Include after-blend additives (blend test mode)
    this.afterBlendAdditives.forEach(additive => {
      if (additive.materialId != null) {
        ids.push(additive.materialId);
      }
    });

    return ids;
  }

  // Helper method to get all selected liquid material IDs (liquid base + liquid additives)
  private getSelectedLiquidMaterialIds(): number[] {
    const ids: number[] = [];

    // Include liquid base if selected
    if (this.rigFreshWater.materialId != null) {
      ids.push(this.rigFreshWater.materialId);
    }

    // Include all liquid additives
    this.liquidAdditives.forEach(additive => {
      if (additive.materialId != null) {
        ids.push(additive.materialId);
      }
    });

    return ids;
  }
}
